import { api } from "@/frontend/lib/api";
import { AddressLookup } from "@/frontend/types";
import { Combobox, Transition } from "@headlessui/react";
import { debounce } from "lodash";
import React, { Fragment, useCallback, useEffect, useState } from "react";

interface FormAddressLookupProps {
    domain: string;
    onSelect: Function;
}

function FormAddressLookup({ domain, onSelect }: FormAddressLookupProps) {
    const [addresses, setAddresses] = useState<AddressLookup[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<AddressLookup | null>(null);
    const [initialLoad, setInitialLoad] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [query, setQuery] = useState("");

    useEffect(() => {
        if (query !== "") {
            setLoading(true);
            fetchAddresses(query);
        }
    }, [query]);

    useEffect(() => {
        if (selectedAddress) {
            fetchAddressSelector(selectedAddress.id);
        }
    }, [selectedAddress]);

    const fetchAddresses = useCallback(
        debounce(async(query: string) => {
            const response = await api.addressLookup({ query, shop_domain: domain });
            setAddresses(response);
            setLoading(false);
            setInitialLoad(true);
        }, 300),
        [domain]
    );

    const fetchAddressSelector = async(id) => {
        const response = await api.addressSelector({ id, shop_domain: domain });
        onSelect({ ...response, id });
        setLoading(false);
    };

    const shouldDisable = (address) => address.type !== "Address";

    return (
        <div className="form-input-wrapper w-full overflow-hidden">
            <Combobox value={selectedAddress} onChange={setSelectedAddress}>
                <Combobox.Input
                    className="form-input"
                    autoComplete="off"
                    onChange={(event) => setQuery(event.target.value)}
                    displayValue={(address: AddressLookup) => (address ? [address.text, address.description].join(", ") : "")}
                />
                {initialLoad && (
                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setQuery("")}
                    >
                        <Combobox.Options
                            className="mt-1 max-h-60 w-full overflow-auto
                            py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5
                            focus:outline-none sm:text-sm"
                        >
                            {!loading && addresses.length === 0 && query !== "" ? (
                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">Nothing found.</div>
                            ) : (
                                addresses.length &&
                                addresses.map((address) => (
                                    <Combobox.Option
                                        key={address.id}
                                        className={({ active, disabled }) =>
                                            `relative cursor-default select-none py-2 pl-4 pr-4 text-sm
                                            ${active ? "bg-blue-500 text-white" : "text-gray-900"}
                                            ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`
                                        }
                                        value={address}
                                        disabled={shouldDisable(address)}
                                    >
                                        {({ selected }) => (
                                            <>
                                                <span className={`block  ${selected ? "font-medium" : "font-normal"}`}>
                                                    {[address.text, address.description].join(", ")}
                                                </span>
                                            </>
                                        )}
                                    </Combobox.Option>
                                ))
                            )}
                        </Combobox.Options>
                    </Transition>
                )}
            </Combobox>
            <label className="form-input-label">Postcode</label>
        </div>
    );
}

export default FormAddressLookup;
